import {
  Directive,
  ElementRef,
  HostListener,
  forwardRef,
  inject,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

/**
 * Checkout-style currency entry: keystrokes are digits interpreted as centavos
 * (value = digits / 100). Display is numeric only (grouping + decimals); place
 * the currency symbol in the textfield lead slot (see gatewee-input-v2).
 *
 * Control value: `number | null`.
 */
@Directive({
  selector: 'input[gateweeCurrencyDigits]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GateweeCurrencyDigitsDirective),
      multi: true,
    },
  ],
})
export class GateweeCurrencyDigitsDirective implements ControlValueAccessor {
  private readonly el = inject(ElementRef<HTMLInputElement>);
  private onChange: (val: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number | null): void {
    if (value === null || value === undefined) {
      this.el.nativeElement.value = '';
      return;
    }
    const n = typeof value === 'number' ? value : Number(value);
    this.el.nativeElement.value = Number.isFinite(n) ? this.format(n) : '';
  }

  registerOnChange(fn: (val: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.el.nativeElement.disabled = disabled;
  }

  @HostListener('input')
  handleInput(): void {
    const el = this.el.nativeElement;
    if (el.disabled || el.readOnly) {
      return;
    }
    const raw = el.value;
    const digits = raw.replace(/\D/g, '');

    if (!digits) {
      this.el.nativeElement.value = '';
      this.onChange(null);
      return;
    }

    const num = parseInt(digits, 10) / 100;
    this.el.nativeElement.value = this.format(num);
    this.onChange(num);
  }

  @HostListener('blur')
  handleBlur(): void {
    this.onTouched();
  }

  /** Numeric display only (no currency symbol); symbol lives in the textfield lead slot. */
  private format(v: number): string {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  }
}
